'use client';

import { useCallback, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { usePrevious } from '@uidotdev/usehooks';
import { sendViewItemListEvent } from '@/features/analytics/datalayer/datalayer';
import { getGTMCategories } from '@/features/analytics/helper';
import { WindowWithDataLayer } from '@/features/analytics/types';
import { PRODUCT_QUERY_PARAMS } from '@/features/navigation/constants';
import { urlSearchParamsDiffer } from '@/features/navigation/utils/url-helpers';
import { NextLevelCategories } from '@/features/filter';
import MobileFacetLoading from '@/features/filter/mobile-facet-loading';
import SortListLoading from '@/features/filter/sort-list/loading';
import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider';
import { Breadcrumb } from '@/features/navigation';
import { handleHidePageLoading } from '@/features/page-loading/utils/show-loading';
import PaginationSkeleton from '@/features/pagination/pagination-skeleton';
import Popup from '@/features/popup';
import { Webengage } from '@/features/webengage';
import BrandCategoriesCombinationListSkeleton from './brand-categories-combination-list-skeleton';
import CombinationsList from './combinations-list';
import FacetSkeleton from './facet-skeleton';
import PLPBanner from './plp-banner';
import PLPGrid from './plp-grid';
import PLPPageTitle from './plp-page-title';
import PLPPagination from './plp-pagination';
import SEODescription from './seo-description';
import { isCombinationMetas, isValidProductListSort } from '../_utils/type-guards';
import { useBasicMetas } from '../_utils/use-basic-metas';
import useBasicMetasInitialLoading from '../_utils/use-basic-metas-initial-loading';
import usePLPData from '../_utils/use-plp-data';
import usePLPInitialLoading from '../_utils/use-products-initial-loading';
import { PAGE_SIZE } from '../constants';
import { usePLPFacetsContext } from '../facets-context/facets-context';
import { PLPPages, PLPSlug } from '../types';
declare const window: WindowWithDataLayer;
const BrandCategoriesCombinationList = dynamic(() => import('./brand-categories-combination-list'), {
  ssr: true,
  loading: () => <BrandCategoriesCombinationListSkeleton />
});
const Facets = dynamic(() => import('./facets'), {
  ssr: true,
  loading: () => <FacetSkeleton />
});
const MobileFacet = dynamic(() => import('@/features/filter/mobile-facet'), {
  loading: () => <MobileFacetLoading />
});
const AppliedFilters = dynamic(() => import('@/features/filter/applied-filters'), {
  ssr: false
});
const SortList = dynamic(() => import('@/features/filter/sort-list'), {
  ssr: false,
  loading: () => <SortListLoading />
});
interface Props {
  page: PLPPages;
  slug?: PLPSlug;
}
function PLPContent({
  page,
  slug
}: Props) {
  const isBasicMetasLoading = useBasicMetasInitialLoading({
    page,
    slug
  });
  const isProductsLoading = usePLPInitialLoading({
    isBasicMetasLoading
  });
  const isInitialLoading = isBasicMetasLoading || isProductsLoading;
  const {
    isMobile
  } = useMobileDetect();
  const pathname = usePathname();
  const router = useRouter();
  const {
    basicMetas
  } = useBasicMetas();
  const {
    pageResponse,
    isLoading,
    isLoadingFirstPage
  } = usePLPData();
  const {
    totalCount = 0,
    pageNumber: lastPageNumber = 1
  } = pageResponse?.products || {};
  const pageCount = totalCount ? Math.ceil(totalCount / PAGE_SIZE) : 0;
  const {
    facetsSearchParams,
    setPageNumber,
    setIsApplyingFacets,
    anyFiltersApplied,
    allFacets,
    filterChangeByUserRef,
    syncFacets,
    setCombinationDefaultFacets,
    searchParams,
    syncSearchParamsWithClientBasicMetas
  } = usePLPFacetsContext();
  const previousFacetsSearchParams = usePrevious(facetsSearchParams);
  useEffect(() => {
    if (pageResponse?.facets) {
      syncFacets(pageResponse.facets);
    }
  }, [pageResponse?.facets, syncFacets]);
  useEffect(() => {
    if (isBasicMetasLoading) return;
    syncSearchParamsWithClientBasicMetas(basicMetas || {});
  }, [syncSearchParamsWithClientBasicMetas, basicMetas, isBasicMetasLoading]);
  useEffect(() => {
    if (page !== PLPPages.Facets) return;
    if (!isCombinationMetas(page, basicMetas)) return;
    if (!basicMetas.facet || !basicMetas.facet.length) return;
    setCombinationDefaultFacets(basicMetas.facet);
  }, [basicMetas, page, setCombinationDefaultFacets]);

  // compare current facetsSearchParams with previous state and check which params has changed
  const facetSearchParamsDifference = useMemo(() => facetsSearchParams && previousFacetsSearchParams ? urlSearchParamsDiffer(facetsSearchParams.toString(), previousFacetsSearchParams.toString()) : null, [facetsSearchParams, previousFacetsSearchParams]);

  // const strigifyFacetSearchParams = facetsSearchParams.toString()
  const onAppliedFacetsChange = useCallback(() => {
    if (!filterChangeByUserRef.current) return;
    const searchParams = new URLSearchParams(facetsSearchParams);
    const newURL = `${pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const currentURL = `${pathname}${window.location.search}`;
    if (newURL === currentURL) return;
    setIsApplyingFacets(true);
    filterChangeByUserRef.current = false;
    router.push(newURL, {
      // scroll to top when page_number or sort params has changed
      scroll: facetSearchParamsDifference?.[PRODUCT_QUERY_PARAMS.PAGE_NUMBER] || facetSearchParamsDifference?.[PRODUCT_QUERY_PARAMS.SORT_BY]
    });
  }, [facetSearchParamsDifference, facetsSearchParams, filterChangeByUserRef, pathname, router, setIsApplyingFacets]);

  // whenever the URL changes, hide loading indicator
  useEffect(() => {
    setIsApplyingFacets(false);
  }, [searchParams, setIsApplyingFacets]);
  useEffect(() => {
    if (!searchParams?.query) return;
    Webengage.trackSearch(searchParams.query);
  }, [searchParams?.query]);
  useEffect(() => {
    // on initial page load, previous state is null. So to prevent unnecessary re-renders, we have to do nothing on first render
    if (!previousFacetsSearchParams) return;
    if (facetSearchParamsDifference?.otherParams && isMobile) {
      return;
    }
    if (facetSearchParamsDifference?.otherParams || facetSearchParamsDifference?.sort) {
      setPageNumber(undefined);
    }
    onAppliedFacetsChange();
  }, [facetsSearchParams, facetSearchParamsDifference?.otherParams, facetSearchParamsDifference?.sort, onAppliedFacetsChange, setPageNumber, previousFacetsSearchParams, isMobile]);

  // send view_item_list event after fetching each page
  useEffect(() => {
    if (!page || !basicMetas || !pageResponse?.products?.items) return;
    const intervalID = setInterval(() => {
      clearInterval(intervalID);
      const categories = page && basicMetas && getGTMCategories(page, basicMetas);
      sendViewItemListEvent(pageResponse, categories);
    }, 500);
    return () => clearInterval(intervalID);
  }, [lastPageNumber]); // eslint-disable-line

  useEffect(() => {
    if (!isInitialLoading) handleHidePageLoading();
  }, [isInitialLoading]);
  const showContent = isLoading || totalCount > 0;
  const showPLPBanner = page === PLPPages.Categories || page === PLPPages.Tags;
  const showNextLevelCategories = page === PLPPages.Categories;
  const showPopup = page === PLPPages.Categories || page === PLPPages.Tags || page === PLPPages.Brands;
  const showCombinationsList = page === PLPPages.Categories;
  const {
    cat_id,
    tag_id,
    brand_id
  } = searchParams || {};
  const stringCategoryId = cat_id?.toString();
  const shouldRenderBrandCategoriesCombinationList = page === PLPPages.Brands && brand_id;
  const currentSortValue = searchParams?.[PRODUCT_QUERY_PARAMS.SORT_BY];
  const defaultSortState = isValidProductListSort(currentSortValue) ? currentSortValue : undefined;
  return <>
            {!isMobile && <Breadcrumb />}
            {showPopup && <Popup cat_id={stringCategoryId} tag_id={tag_id?.toString()} brand_id={brand_id?.toString()} />}
            <div className='relative mb-10 flex grow flex-col lg:flex-row lg:gap-10'>
                {isLoadingFirstPage ? <section className='hidden w-[221px] min-w-[221px] flex-col lg:flex'>
                        <FacetSkeleton />
                    </section> : <section className='hidden w-[221px] min-w-[221px] flex-col lg:flex'>
                        {showNextLevelCategories && !isMobile && <NextLevelCategories mode='desktop' />}

                        {!isMobile && showCombinationsList && <CombinationsList cat_id={stringCategoryId} />}

                        {!!allFacets && <Facets />}
                    </section>}

                <section className='flex min-h-[calc(100dvh_-_68px)] w-full grow flex-col lg:min-h-fit'>
                    {isMobile && <Breadcrumb />}
                    {showPLPBanner && page !== PLPPages.Tags && <PLPBanner searchParams={searchParams} />}
                    {isMobile && <PLPPageTitle />}

                    {isMobile && showCombinationsList && <CombinationsList cat_id={stringCategoryId} />}

                    {isMobile && showPLPBanner && page === PLPPages.Tags && <PLPBanner page={page} basicMetas={basicMetas} searchParams={searchParams} />}

                    {showNextLevelCategories && isMobile && <NextLevelCategories mode='mobile' />}
                    <div className='mb-4 flex justify-between lg:mb-0 lg:flex-col'>
                        <MobileFacet onAppliedFacetsChange={() => {
            filterChangeByUserRef.current = true;
            setPageNumber(undefined);
            onAppliedFacetsChange();
          }} data-sentry-element="MobileFacet" data-sentry-source-file="plp-content.tsx">
                            {anyFiltersApplied && <div className='flex flex-row flex-wrap gap-2'>
                                    <AppliedFilters />
                                </div>}
                            <Facets mode='mobile' data-sentry-element="Facets" data-sentry-source-file="plp-content.tsx" />
                        </MobileFacet>
                        <div className='flex items-center justify-between lg:mb-4 lg:grow'>
                            {!isMobile && <PLPPageTitle />}

                            {showContent && <SortList defaultState={defaultSortState} />}
                        </div>
                    </div>
                    {!isMobile && showPLPBanner && page === PLPPages.Tags && <PLPBanner page={page} basicMetas={basicMetas} searchParams={searchParams} />}
                    <div className='hidden lg:flex'>
                        <AppliedFilters data-sentry-element="AppliedFilters" data-sentry-source-file="plp-content.tsx" />
                    </div>

                    <PLPGrid isInitialLoading={isInitialLoading} data-sentry-element="PLPGrid" data-sentry-source-file="plp-content.tsx" />

                    {isLoading ? <PaginationSkeleton /> : <div data-test='plp-pagination'>
                            <PLPPagination onPageChange={pageNum => {
            filterChangeByUserRef.current = true;
            setPageNumber(pageNum);
          }} pageCount={pageCount} />
                        </div>}

                    {shouldRenderBrandCategoriesCombinationList && <BrandCategoriesCombinationList brand_id={brand_id?.toString()} />}
                    {!isMobile && <SEODescription />}
                </section>

                {isMobile && <SEODescription />}
            </div>
        </>;
}
export default PLPContent;